<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <button type="button" class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="handleSave" :disabled="submitting">
          <span v-if="submitting">儲存中...</span>
          <span v-else>儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="loadStoreHours">重試</button>
    </div>

    <div v-else class="hours-content">
      <div class="info-section">
        <h3 class="section-title">營業時間設定</h3>
        <p class="section-hint">設定每週營業時段，每天可新增多個時段（可表達多段營業與多個休息區間）</p>
      </div>

      <div class="hours-table">
        <table>
          <thead>
            <tr>
              <th>星期</th>
              <th>是否營業</th>
              <th>營業時段</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(day, index) in days" :key="index">
              <td class="day-name">{{ day.label }}</td>
              <td>
                <label class="checkbox-label">
                  <input
                    v-model="hours[index].isOpen"
                    type="checkbox"
                    class="form-checkbox"
                    @change="handleDayToggle(index)"
                  />
                  <span>{{ hours[index].isOpen ? '營業' : '休息' }}</span>
                </label>
              </td>
              <td>
                <div v-if="hours[index].isOpen" class="slots-container">
                  <div
                    v-for="(slot, slotIndex) in hours[index].timeSlots"
                    :key="`${index}-${slotIndex}`"
                    class="slot-row"
                  >
                    <input
                      v-model="slot.openTime"
                      type="time"
                      class="form-control time-input"
                    />
                    <span class="slot-separator">~</span>
                    <input
                      v-model="slot.closeTime"
                      type="time"
                      class="form-control time-input"
                    />
                    <button
                      type="button"
                      class="btn btn-sm btn-danger"
                      @click="removeSlot(index, slotIndex)"
                    >
                      刪除
                    </button>
                  </div>
                  <button type="button" class="btn btn-sm btn-secondary" @click="addSlot(index)">
                    + 新增時段
                  </button>
                </div>
                <span v-else class="closed-text">休息日</span>
                <div v-if="errors[`day${index}`]" class="invalid-feedback">
                  {{ errors[`day${index}`] }}
                </div>
                <div
                  v-for="(slot, slotIndex) in hours[index].timeSlots"
                  :key="`error-${index}-${slotIndex}`"
                >
                  <div v-if="errors[`day${index}_slot${slotIndex}`]" class="invalid-feedback">
                    {{ errors[`day${index}_slot${slotIndex}`] }}
                  </div>
                </div>
                <div class="copy-actions">
                  <button type="button" class="btn btn-sm btn-secondary" @click="copyDaySlots(index, 'weekdays')">
                    複製到平日
                  </button>
                  <button type="button" class="btn btn-sm btn-secondary" @click="copyDaySlots(index, 'weekend')">
                    複製到假日
                  </button>
                  <button type="button" class="btn btn-sm btn-secondary" @click="copyDaySlots(index, 'all')">
                    複製到全部
                  </button>
                  <div class="copy-single-day">
                    <select
                      v-model="copyTargetDay[index]"
                      class="form-control copy-day-select"
                    >
                      <option value="">指定天...</option>
                      <option
                        v-for="targetDay in days.filter(d => d.value !== index)"
                        :key="`target-${index}-${targetDay.value}`"
                        :value="targetDay.value"
                      >
                        {{ targetDay.label }}
                      </option>
                    </select>
                    <button type="button" class="btn btn-sm btn-secondary" @click="copyToSingleDay(index)">
                      複製
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { storeService } from '@/api/modules/stores.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'StoreHours',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('營業時間設定');
    const storeId = route.params.id;
    
    const days = [
      { label: '週日', value: 0 },
      { label: '週一', value: 1 },
      { label: '週二', value: 2 },
      { label: '週三', value: 3 },
      { label: '週四', value: 4 },
      { label: '週五', value: 5 },
      { label: '週六', value: 6 },
    ];

    const createDefaultSlot = () => ({ openTime: '09:00', closeTime: '18:00' });
    const createDefaultDay = (dayOfWeek) => ({
      dayOfWeek,
      isOpen: false,
      timeSlots: [],
    });
    const hours = ref(Array.from({ length: 7 }, (_, i) => createDefaultDay(i)));

    const loading = ref(false);
    const submitting = ref(false);
    const error = ref(null);
    const errors = ref({});
    const copyTargetDay = ref({});

    const cloneSlots = (slots = []) => {
      if (!Array.isArray(slots)) return [];
      return slots.map(slot => ({
        displayName: slot.displayName || '',
        openTime: slot.openTime,
        closeTime: slot.closeTime
      }));
    };

    const loadStoreHours = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await storeService.getStoreHours(storeId);
        if (response && response.success && response.data) {
          const loadedHours = response.data.hours || [];
          
          // 初始化一週七天資料，兼容舊版 open/close/break 欄位
          for (let i = 0; i < 7; i++) {
            const existingHour = loadedHours.find(h => h.dayOfWeek === i);
            if (existingHour) {
              let normalizedSlots = [];
              if (Array.isArray(existingHour.timeSlots) && existingHour.timeSlots.length > 0) {
                normalizedSlots = existingHour.timeSlots
                  .filter(slot => slot && slot.openTime && slot.closeTime)
                  .map(slot => ({
                    displayName: slot.displayName || '',
                    openTime: slot.openTime,
                    closeTime: slot.closeTime
                  }));
              } else if (existingHour.openTime && existingHour.closeTime) {
                if (existingHour.breakStart && existingHour.breakEnd) {
                  normalizedSlots = [
                    { openTime: existingHour.openTime, closeTime: existingHour.breakStart },
                    { openTime: existingHour.breakEnd, closeTime: existingHour.closeTime },
                  ];
                } else {
                  normalizedSlots = [{ openTime: existingHour.openTime, closeTime: existingHour.closeTime }];
                }
              }

              hours.value[i] = {
                dayOfWeek: i,
                isOpen: existingHour.isOpen || normalizedSlots.length > 0,
                timeSlots: normalizedSlots.length > 0 ? normalizedSlots : (existingHour.isOpen ? [createDefaultSlot()] : []),
              };
            } else {
              hours.value[i] = createDefaultDay(i);
            }
          }
        }
      } catch (err) {
        console.error('載入營業時間失敗:', err);
        error.value = err.message || '載入營業時間失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleDayToggle = (index) => {
      // 當切換為不營業時，清空時間
      if (!hours.value[index].isOpen) {
        hours.value[index].timeSlots = [];
      } else {
        if (!hours.value[index].timeSlots.length) {
          hours.value[index].timeSlots = [createDefaultSlot()];
        }
      }
    };

    const addSlot = (dayIndex) => {
      hours.value[dayIndex].timeSlots.push(createDefaultSlot());
    };

    const removeSlot = (dayIndex, slotIndex) => {
      if (hours.value[dayIndex].timeSlots.length <= 1) return;
      hours.value[dayIndex].timeSlots.splice(slotIndex, 1);
    };

    const applyCopyToTargetDays = (sourceIndex, targetDayIndexes) => {
      const sourceDay = hours.value[sourceIndex];
      const sourceSlots = cloneSlots(sourceDay.timeSlots);

      targetDayIndexes.forEach(targetIndex => {
        if (targetIndex === sourceIndex) return;
        hours.value[targetIndex].isOpen = sourceDay.isOpen;
        hours.value[targetIndex].timeSlots = sourceDay.isOpen ? cloneSlots(sourceSlots) : [];
      });
    };

    const copyDaySlots = (sourceIndex, mode) => {
      let targetDays = [];
      if (mode === 'weekdays') {
        targetDays = [1, 2, 3, 4, 5];
      } else if (mode === 'weekend') {
        targetDays = [0, 6];
      } else {
        targetDays = [0, 1, 2, 3, 4, 5, 6];
      }
      applyCopyToTargetDays(sourceIndex, targetDays);
    };

    const copyToSingleDay = (sourceIndex) => {
      const targetIndex = Number(copyTargetDay.value[sourceIndex]);
      if (Number.isNaN(targetIndex)) return;
      applyCopyToTargetDays(sourceIndex, [targetIndex]);
      copyTargetDay.value[sourceIndex] = '';
    };

    const validateHours = () => {
      errors.value = {};

      for (let i = 0; i < hours.value.length; i++) {
        const day = hours.value[i];
        
        if (day.isOpen) {
          if (!Array.isArray(day.timeSlots) || day.timeSlots.length === 0) {
            errors.value[`day${i}`] = '請至少新增一個營業時段';
            continue;
          }

          const sortedSlots = [...day.timeSlots]
            .map((slot, idx) => ({ ...slot, idx }))
            .sort((a, b) => a.openTime.localeCompare(b.openTime));

          sortedSlots.forEach((slot, sortedIdx) => {
            if (!slot.openTime || !slot.closeTime) {
              errors.value[`day${i}_slot${slot.idx}`] = '請輸入完整開始與結束時間';
              return;
            }
            if (slot.openTime >= slot.closeTime) {
              errors.value[`day${i}_slot${slot.idx}`] = '結束時間必須晚於開始時間';
              return;
            }
            if (sortedIdx > 0 && slot.openTime < sortedSlots[sortedIdx - 1].closeTime) {
              errors.value[`day${i}_slot${slot.idx}`] = '時段不可重疊';
            }
          });
        }
      }

      return Object.keys(errors.value).length === 0;
    };

    const buildHoursPayload = () => {
      return hours.value
        .filter(day => day.isOpen)
        .map(day => ({
          dayOfWeek: day.dayOfWeek,
          isOpen: true,
          timeSlots: [...day.timeSlots]
            .sort((a, b) => a.openTime.localeCompare(b.openTime))
            .map(slot => ({
              displayName: String(slot.displayName || '').trim(),
              openTime: slot.openTime,
              closeTime: slot.closeTime
            })),
        }));
    };

    const handleSave = async () => {
      if (!validateHours()) {
        await showError('驗證失敗', '請檢查輸入的營業時間');
        return;
      }

      submitting.value = true;
      errors.value = {};

      try {
        const hoursToSave = buildHoursPayload();

        const response = await storeService.setStoreHours(storeId, hoursToSave);

        if (response && response.success) {
          await showSuccess('儲存成功', '營業時間已更新');
          router.push(`/client/stores/${storeId}`);
        } else {
          const errorMsg = (response && response.message) || '儲存營業時間失敗';
          await showError('儲存失敗', errorMsg);
        }
      } catch (err) {
        console.error('儲存營業時間失敗:', err);
        
        let errorTitle = '儲存失敗';
        let errorText = err.message || '儲存營業時間失敗，請稍後再試';

        if (err.code === 'VALIDATION_ERROR') {
          errorText = err.message || '資料驗證失敗，請檢查輸入的資料';
        }

        await showError(errorTitle, errorText);
      } finally {
        submitting.value = false;
      }
    };

    const handleBack = () => {
      router.push(`/client/stores/${storeId}`);
    };

    onMounted(() => {
      loadStoreHours();
    });

    return {
      days,
      hours,
      loading,
      submitting,
      error,
      errors,
      copyTargetDay,
      pageTitle,
      loadStoreHours,
      handleDayToggle,
      addSlot,
      removeSlot,
      copyDaySlots,
      copyToSingleDay,
      handleSave,
      handleBack,
    };
  },
};
</script>

<style scoped>
.page-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.hours-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.info-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.section-hint {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
}

.hours-table {
  overflow-x: auto;
  margin-bottom: 24px;
}

.hours-table table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.hours-table th,
.hours-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.hours-table th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.day-name {
  font-weight: 500;
  min-width: 60px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.form-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.time-input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;
  min-width: 120px;
}

.slots-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.slot-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.slot-separator {
  color: #6b7280;
}

.closed-text {
  color: #9ca3af;
}

.btn-sm {
  padding: 6px 10px;
  font-size: 12px;
}

.copy-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  align-items: center;
}

.copy-single-day {
  display: flex;
  gap: 8px;
  align-items: center;
}

.copy-day-select {
  min-width: 120px;
  padding: 6px 8px;
  font-size: 12px;
}

.btn-danger {
  background-color: #ef4444;
  color: #fff;
}

.btn-danger:hover {
  background-color: #dc2626;
}

.time-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.time-input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.time-input.is-invalid {
  border-color: #ef4444;
}

.invalid-feedback {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #ef4444;
  margin-bottom: 16px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background-color: #4b5563;
}

@media (max-width: 768px) {
  .hours-table {
    overflow-x: scroll;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
